import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { PenLine, Minus } from 'lucide-react';

const STORAGE_KEY_PREFIX = 'quicknotes_data';

function getStorageKey() {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    const email = user?.email;
    if (!email) return null;
    return `${STORAGE_KEY_PREFIX}_${email}`;
  } catch { return null; }
}

function loadPersistedState() {
  try {
    const key = getStorageKey();
    if (!key) return { content: '', pos: null, minimized: true };
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { content: '', pos: null, minimized: true };
}

function persist(state) {
  try {
    const key = getStorageKey();
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(state));
  } catch {}
}

function defaultPos() {
  return { left: 24, top: Math.max(60, window.innerHeight - 420) };
}

function clampPos(pos, w = 300, h = 360) {
  return {
    left: Math.max(0, Math.min(window.innerWidth - w, pos.left)),
    top: Math.max(0, Math.min(window.innerHeight - 60, pos.top)),
  };
}

const isMac = () => navigator.platform.toUpperCase().indexOf('MAC') >= 0;

export default function QuickNotes() {
  const { isDarkMode } = useTheme();
  const [state, setState] = useState(loadPersistedState);
  const [lastSaved, setLastSaved] = useState(() => loadPersistedState().content ? Date.now() : null);
  const [savedAgo, setSavedAgo] = useState('just now');
  const debounceRef = useRef(null);
  const cardRef = useRef(null);
  const textareaRef = useRef(null);
  const isDraggingRef = useRef(false);
  const shortcutHint = 'F2';

  const { content, pos, minimized } = state;
  const effectivePos = clampPos(pos || defaultPos());

  const patchState = useCallback((patch) => {
    setState(prev => {
      const next = { ...prev, ...patch };
      persist(next);
      return next;
    });
  }, []);

  // Content change — update state immediately, debounce save
  const handleContentChange = (e) => {
    const val = e.target.value;
    setState(prev => ({ ...prev, content: val }));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setState(prev => {
        persist(prev);
        return prev;
      });
      setLastSaved(Date.now());
    }, 1000);
  };

  // "Saved X ago" display ticker
  useEffect(() => {
    if (!lastSaved) return;
    const tick = () => {
      const s = Math.floor((Date.now() - lastSaved) / 1000);
      if (s < 5) setSavedAgo('just now');
      else if (s < 60) setSavedAgo(`${s}s ago`);
      else setSavedAgo(`${Math.floor(s / 60)}m ago`);
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [lastSaved]);

  // Keyboard shortcuts — capture phase so textarea doesn't swallow combos
  useEffect(() => {
    const handleKey = (e) => {
      // Toggle: F2 (all platforms)
      if (e.key === 'F2') {
        e.preventDefault();
        setState(prev => {
          const next = { ...prev, minimized: !prev.minimized };
          persist(next);
          return next;
        });
        return;
      }

      // Force save: Cmd+S (Mac) or Ctrl+S (Win/Linux)
      const isSave = (isMac() ? e.metaKey : e.ctrlKey) && (e.key === 's' || e.key === 'S');
      if (isSave) {
        setState(prev => {
          if (prev.minimized) return prev;
          e.preventDefault();
          persist(prev);
          return prev;
        });
        setLastSaved(Date.now());
        return;
      }

      // Escape: minimize
      if (e.key === 'Escape') {
        setState(prev => {
          if (prev.minimized) return prev;
          const next = { ...prev, minimized: true };
          persist(next);
          return next;
        });
      }
    };
    document.addEventListener('keydown', handleKey, true);
    return () => document.removeEventListener('keydown', handleKey, true);
  }, []);

  // Focus textarea when expanding
  useEffect(() => {
    if (!minimized) {
      const t = setTimeout(() => textareaRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [minimized]);

  // Cleanup debounce on unmount
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Drag — mutate DOM directly for performance, save on mouseup
  const handleHeaderMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    isDraggingRef.current = true;
    const rect = cardRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    const onMove = (e) => {
      if (!isDraggingRef.current) return;
      const l = Math.max(0, Math.min(window.innerWidth - 300, e.clientX - offsetX));
      const t = Math.max(0, Math.min(window.innerHeight - 60, e.clientY - offsetY));
      cardRef.current.style.left = l + 'px';
      cardRef.current.style.top = t + 'px';
    };

    const onUp = () => {
      isDraggingRef.current = false;
      const r = cardRef.current.getBoundingClientRect();
      patchState({ pos: { left: r.left, top: r.top } });
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const isEmpty = !content.trim();
  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;

  // Theme tokens
  const bg     = isDarkMode ? '#111827' : '#ffffff';
  const hdrBg  = isDarkMode ? '#0f172a' : '#f8fafc';
  const border = isDarkMode ? '1px solid #1e293b' : '1px solid #e2e8f0';
  const txt    = isDarkMode ? '#f1f5f9' : '#111827';
  const sub    = isDarkMode ? '#64748b' : '#9ca3af';
  const muted  = isDarkMode ? '#374151' : '#f1f5f9';
  const mutedTxt = isDarkMode ? '#9ca3af' : '#6b7280';
  const shadow = isDarkMode
    ? '0 20px 60px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4)'
    : '0 20px 60px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06)';

  const CHIPS = [
    '📝 Terminology notes',
    '🔤 Glossary terms',
    '❓ Client questions',
    '📋 Reviewer instructions',
  ];
  const SHORTCUTS = [
    ['F2', 'Open / Close'],
    ['Esc', 'Minimize'],
    [isMac() ? '⌘S' : 'Ctrl+S', 'Save now'],
  ];
  const TIPS = [
    'Start typing — saves automatically',
    'Drag header to reposition panel',
    'Notes persist across sessions',
  ];

  // FAB (minimized)
  if (minimized) {
    return (
      <button
        onClick={() => patchState({ minimized: false })}
        title={`Quick Notes (${shortcutHint})`}
        style={{
          position: 'fixed', bottom: 24, left: 24, zIndex: 9990,
          width: 44, height: 44, borderRadius: '50%',
          background: '#2563eb', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(37,99,235,0.45)',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 22px rgba(37,99,235,0.55)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 14px rgba(37,99,235,0.45)';
        }}
      >
        <PenLine size={19} color="#fff" strokeWidth={2.2} />
      </button>
    );
  }

  // Expanded card
  return (
    <div
      ref={cardRef}
      style={{
        position: 'fixed',
        left: effectivePos.left,
        top: effectivePos.top,
        zIndex: 9990,
        width: 300,
        height: 360,
        display: 'flex',
        flexDirection: 'column',
        background: bg,
        border,
        borderRadius: 16,
        boxShadow: shadow,
        overflow: 'hidden',
      }}
    >
      {/* Header / drag handle */}
      <div
        onMouseDown={handleHeaderMouseDown}
        style={{
          background: hdrBg,
          borderBottom: border,
          padding: '9px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          cursor: 'grab',
          flexShrink: 0,
          userSelect: 'none',
        }}
      >
        <PenLine size={13} color="#2563eb" strokeWidth={2.2} />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: txt }}>Quick Notes</span>
        <button
          onMouseDown={e => e.stopPropagation()}
          onClick={() => patchState({ minimized: true })}
          title="Minimize (Esc)"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 4, color: sub, borderRadius: 6, lineHeight: 0,
          }}
        >
          <Minus size={14} />
        </button>
      </div>

      {/* Body — textarea always mounted; empty state overlays when blank */}
      <div
        style={{ flex: 1, position: 'relative', overflow: 'hidden', cursor: isEmpty ? 'text' : 'auto' }}
        onClick={() => { if (isEmpty) textareaRef.current?.focus(); }}
      >
        {/* Textarea — transparent when empty so empty state shows through */}
        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleContentChange}
          spellCheck
          style={{
            position: 'absolute', inset: 0,
            resize: 'none',
            border: 'none',
            outline: 'none',
            padding: '12px 14px',
            background: bg,
            color: txt,
            fontSize: 13,
            lineHeight: 1.65,
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif',
            caretColor: '#2563eb',
            opacity: isEmpty ? 0 : 1,
            zIndex: isEmpty ? 0 : 1,
          }}
        />

        {/* Empty state — pointer-events:none so parent click reaches the overlay handler */}
        {isEmpty && (
          <div style={{
            position: 'absolute', inset: 0,
            overflowY: 'auto',
            padding: '14px 14px 10px',
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}>
            {/* Hero */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                background: isDarkMode ? '#1e3a5f' : '#dbeafe',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <PenLine size={15} color="#2563eb" strokeWidth={2.2} />
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: txt, lineHeight: 1.2 }}>
                  Your quick scratch pad
                </p>
                <p style={{ margin: 0, fontSize: 11, color: mutedTxt, lineHeight: 1.3, marginTop: 2 }}>
                  Jot notes while you work — never lose context
                </p>
              </div>
            </div>

            {/* Use-case chips */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {CHIPS.map(chip => (
                <span key={chip} style={{
                  fontSize: 10, fontWeight: 500,
                  padding: '3px 8px',
                  borderRadius: 20,
                  background: muted,
                  color: mutedTxt,
                  whiteSpace: 'nowrap',
                }}>{chip}</span>
              ))}
            </div>

            {/* Shortcuts */}
            <div style={{
              background: muted,
              borderRadius: 10,
              padding: '8px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}>
              {SHORTCUTS.map(([key, label]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 600, fontFamily: 'monospace',
                    background: isDarkMode ? '#111827' : '#e2e8f0',
                    color: isDarkMode ? '#93c5fd' : '#2563eb',
                    padding: '1px 6px', borderRadius: 5,
                  }}>{key}</span>
                  <span style={{ fontSize: 11, color: mutedTxt }}>{label}</span>
                </div>
              ))}
            </div>

            {/* Tips */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {TIPS.map(tip => (
                <p key={tip} style={{
                  margin: 0, fontSize: 11, color: mutedTxt, lineHeight: 1.4,
                  paddingLeft: 10, position: 'relative',
                }}>
                  <span style={{
                    position: 'absolute', left: 0, top: 4,
                    width: 4, height: 4, borderRadius: '50%',
                    background: isDarkMode ? '#374151' : '#d1d5db',
                    display: 'inline-block',
                  }} />
                  {tip}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          background: hdrBg,
          borderTop: border,
          padding: '6px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 11, color: sub }}>
          {wordCount} {wordCount === 1 ? 'word' : 'words'}
        </span>
        <span style={{ fontSize: 11, color: sub }}>
          {lastSaved ? `Saved ${savedAgo}` : 'Not saved yet'}
        </span>
      </div>
    </div>
  );
}
